import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import LabAssistCaseDetail from './LabAssistCaseDetail';
import LabOperationsPage from './LabOperationsPage';
import LabRunDetail from './LabRunDetail';
import LabRunList from './LabRunList';
import LabScenarioDetail from './LabScenarioDetail';
import LabScenarioList from './LabScenarioList';

export type LabRoutesProps = {
  /**
   * Steward and proctor actions: stocking pools, assigning pods, enrolling agents, pushing a
   * step mid-session, waiving criteria, re-opening validation.
   *
   * This decides what is offered, not what is permitted. Every one of those calls is authorized
   * again on the backend against the caller's token - the pool contact or staff for pool work,
   * staff for an unscoped push - so a caller who reaches an endpoint some other way is refused
   * there rather than here.
   */
  canSteward?: boolean;
};

/**
 * Two halves, kept apart deliberately.
 *
 * `/scenarios` is authoring: it writes to the generic CRUD API, and nothing there touches a
 * running pod. `/runs` and `/operations` are the lab controller: they act on pods that are
 * already live and cannot change what a scenario says. The one crossing point is pushing a
 * step mid-session, which starts from a saved step and goes out through the controller.
 */
const LabRoutes: React.FC<LabRoutesProps> = ({ canSteward = false }) => (
  <Routes>
    <Route index element={<Navigate to="scenarios" replace />} />

    <Route path="scenarios" element={<LabScenarioList canEdit={canSteward} />} />
    <Route path="scenarios/:scenarioId" element={<LabScenarioDetail canEdit={canSteward} />} />

    <Route path="runs" element={<LabRunList />} />
    <Route path="runs/:runId" element={<LabRunDetail canProctor={canSteward} />} />

    <Route path="operations" element={<LabOperationsPage canSteward={canSteward} />} />
    <Route path="assist/:reference" element={<LabAssistCaseDetail />} />

    <Route path="*" element={<Navigate to="scenarios" replace />} />
  </Routes>
);

export default LabRoutes;
